import { useEffect, memo, useRef, useCallback } from 'react'
import { reaction } from 'mobx'
import { RouteComponentProps } from 'react-router'
import { DependencyProvider } from '@finanzcheck/catalyst-conditional-component'
import formDependencies from 'utils/formDependencies/formDependencies'
import { useData } from 'stores/utils/hooks/useData'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import CategoryLayout from 'components/Layout/Category'
import { ConfigProps } from 'config/config.interface'
import { observer } from 'mobx-react'
import { getFirstInput, getSkeletonElements } from 'utils/input'
import PageComponents from './PageComponents'

export type PageProps = RouteComponentProps & ConfigProps

const Page = observer(
  ({
    options: {
      reactions,
      components,
      isFooterVisible = true,
      largeCategory = false
    }
  }: PageProps) => {
    const { rootStore } = useRootStore()
    const {
      navigation: { stack },
      setIsFooterVisible
    } = rootStore
    const { data } = useData()

    useEffect(() => {
      if (!reactions) {
        return
      }

      reactions(rootStore).map(({ field, method }) => reaction(field, method))
    }, [reactions, rootStore])

    useEffect(() => {
      if (isFooterVisible) {
        return undefined
      }

      setIsFooterVisible(false)

      return () => {
        setIsFooterVisible(true)
      }
    }, [isFooterVisible, setIsFooterVisible])

    const categoryRef = useRef<HTMLDivElement | null>(null)

    const onObserve = useCallback<MutationCallback>(
      (mutationsList, formObserver) => {
        if (!categoryRef.current) {
          return
        }

        for (const { removedNodes } of mutationsList) {
          if (removedNodes.length === 0) {
            continue
          }

          if (getSkeletonElements(categoryRef.current)?.length === 0) {
            getFirstInput()?.focus()
            formObserver.disconnect()
            return
          }
        }
      },
      []
    )

    useEffect(() => {
      if (stack.length === 1) {
        return undefined
      }

      if (!categoryRef.current) {
        return undefined
      }

      const formObserver = new MutationObserver(onObserve)

      formObserver.observe(categoryRef.current, {
        childList: true,
        subtree: true
      })

      return () => {
        formObserver.disconnect()
      }
    }, [onObserve, stack])

    return (
      <CategoryLayout ref={categoryRef} largeCategory={largeCategory}>
        <DependencyProvider state={data} dependencies={formDependencies}>
          <PageComponents components={components} />
        </DependencyProvider>
      </CategoryLayout>
    )
  }
)

export default memo(Page)
